// src/layout/Sidebar.jsx
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Divider,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import CollectionsBookmarkRoundedIcon from "@mui/icons-material/CollectionsBookmarkRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import LocalOfferRoundedIcon from "@mui/icons-material/LocalOfferRounded";
import ArticleRoundedIcon from "@mui/icons-material/ArticleRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import useLayoutStore from "../store/useLayoutStore";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import logoFull from "../assets/logo-full.png";
import logoSmall from "../assets/logo-small.png";
import ConfirmationNumberRoundedIcon from "@mui/icons-material/ConfirmationNumberRounded";
import PhotoAlbumRoundedIcon from "@mui/icons-material/PhotoAlbumRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import CasinoRoundedIcon from "@mui/icons-material/CasinoRounded";

const Sidebar = () => {
  const { isSidebarOpen, mobileSidebarOpen, setMobileSidebarOpen } =
    useLayoutStore();
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();

  // ✅ Phân chia breakpoint
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <600
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600–899
  const isLaptop = useMediaQuery(theme.breakpoints.between("md", "lg")); // 900–1199
  const isDesktop = useMediaQuery(theme.breakpoints.between("lg", "xl")); // 1200–1535
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("xl")); // ≥1536

  // ✅ Menu
  const menuItems = [
    {
      text: t("Dashboard"),
      icon: <DashboardRoundedIcon />,
      path: "/dashboard",
    },
    { text: t("Products"), icon: <Inventory2RoundedIcon />, path: "/products" },
    {
      text: t("Collections"),
      icon: <CollectionsBookmarkRoundedIcon />,
      path: "/collections",
    },
    { text: t("Orders"), icon: <ReceiptLongRoundedIcon />, path: "/orders" },
    {
      text: t("Customers"),
      icon: <PeopleAltRoundedIcon />,
      path: "/customers",
    },
    { text: t("CMS"), icon: <ArticleRoundedIcon />, path: "/cms" },
    { text: t("Report"), icon: <InsertChartRoundedIcon />, path: "/reports" },
    {
      text: t("Promotions"),
      icon: <LocalOfferRoundedIcon />,
      path: "/promotions",
    },
    {
      text: t("Coupons"),
      icon: <ConfirmationNumberRoundedIcon />,
      path: "/coupons",
    },
    { text: t("Albums"), icon: <PhotoAlbumRoundedIcon />, path: "/albums" },
    { text: t("Sales"), icon: <TrendingUpRoundedIcon />, path: "/sales" },
    {
      text: t("Voucher Game"),
      icon: <CasinoRoundedIcon />,
      path: "/voucher-game",
    },
    {
      text: "Vouchers",
      path: "/admin/vouchers",
      icon: "🎟️",
    },
  ];

  // ✅ Tính chiều rộng Sidebar theo loại màn hình
  let paperWidth = 0;
  let showText = true;

  if (isMobile) {
    paperWidth = 240; // full width overlay
    showText = true;
  } else if (isTablet) {
    paperWidth = 200; // hẹp hơn
    showText = true;
  } else if (isLaptop) {
    paperWidth = isSidebarOpen ? 240 : 80;
    showText = isSidebarOpen;
  } else if (isDesktop) {
    paperWidth = isSidebarOpen ? 280 : 100;
    showText = isSidebarOpen;
  } else if (isLargeScreen) {
    paperWidth = isSidebarOpen ? 320 : 120;
    showText = isSidebarOpen;
  }

  return (
    <Drawer
      variant={isMobile || isTablet ? "temporary" : "permanent"}
      open={isMobile || isTablet ? mobileSidebarOpen : true}
      onClose={() => setMobileSidebarOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: paperWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: paperWidth,
          overflowX: "hidden",
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary,
          transition: "width 0.3s",
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      {/* Logo */}
      <Toolbar sx={{ justifyContent: "center", minHeight: 64 }}>
        <Box sx={{ py: 1 }}>
          <img
            src={showText ? logoFull : logoSmall}
            alt="Logo"
            style={{
              width: showText ? "140px" : "40px",
              height: "auto",
              objectFit: "contain",
              transition: "0.3s ease",
            }}
          />
        </Box>
      </Toolbar>

      <Divider sx={{ mx: "auto", width: "80%", mb: 1 }} />

      {/* Menu */}
      <List disablePadding>
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);
          return (
            <Tooltip
              key={item.text}
              title={!showText ? item.text : ""}
              placement="right"
              arrow
            >
              <ListItemButton
                component={Link}
                to={item.path}
                selected={isActive}
                onClick={
                  isMobile || isTablet
                    ? () => setMobileSidebarOpen(false)
                    : undefined
                }
                sx={{
                  mx: 1,
                  mb: 1,
                  borderRadius: 2,
                  px: showText ? 2 : 1,
                  justifyContent: showText ? "flex-start" : "center",
                  backgroundColor: isActive
                    ? theme.palette.action.selected
                    : "transparent",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: showText ? 2 : 0,
                    justifyContent: "center",
                    color: isActive
                      ? theme.palette.primary.main
                      : theme.palette.text.secondary,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {showText && (
                  <ListItemText
                    primary={item.text}
                    sx={{ whiteSpace: "nowrap" }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          );
        })}
      </List>
    </Drawer>
  );
};

export default Sidebar;
